import React, { useState } from 'react';
import classNames from 'classnames';

import { icon_chevron_down } from '@gn-audio/icons';
import { JabraIcon, useFallbackTranslation } from '@gn-audio/jss-components';

import ImageTabSlider from './ImageTabSlider';
import TabNavigationItem from './TabNavigationItem';
import { TabNavigationItemFields, TabNavigationItemType } from './tabNavigationSpotTypes';

type MobileTabNavigationProps = {
  navigationType: TabNavigationItemType;
  tabs: TabNavigationItemFields[] | undefined;
  activeIndex: number | null;
  handleTabClick: (index: number) => void;
  instanceId?: number;
  ariaLabel?: string;
};

const MobileTabNavigation: React.FC<MobileTabNavigationProps> = ({
  navigationType,
  tabs,
  activeIndex,
  handleTabClick,
  instanceId,
  ariaLabel,
}) => {
  const { t } = useFallbackTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  switch (navigationType) {
    case 'image': {
      return (
        <div className="w-full md:hidden">
          <ImageTabSlider
            tabs={tabs}
            activeIndex={activeIndex}
            handleTabClick={handleTabClick}
            instanceId={instanceId}
          />
        </div>
      );
    }

    case 'icon': {
      const getGridColsClass = () => {
        const tabsCount = tabs?.length || 0;

        return classNames('grid-cols-2', {
          'sm:grid-cols-2': tabsCount > 2 && tabsCount <= 4,
          'sm:grid-cols-3': tabsCount > 4 && tabsCount <= 6,
          'sm:grid-cols-4': tabsCount > 6,
        });
      };

      return (
        <div
          className={classNames(
            'grid w-full gap-1 rounded-[58px] bg-[var(--tab-bg-color)] text-[var(--tab-text-color)] md:hidden',
            getGridColsClass()
          )}
          role="tablist"
          aria-label={ariaLabel}
          aria-orientation="vertical"
        >
          {tabs?.map((tab, index) => (
            <div key={`mobile-tab-${tab.id}`} className="flex items-center justify-center">
              <TabNavigationItem
                tab={tab}
                index={index}
                navigationType={navigationType}
                activeIndex={activeIndex}
                handleTabClick={handleTabClick}
                instanceId={instanceId}
              />
            </div>
          ))}
        </div>
      );
    }

    default:
      return (
        <div className="relative md:hidden">
          <div
            className={classNames(
              'flex w-full items-center bg-[var(--tab-bg-color)]',
              isDropdownOpen ? 'rounded-t-[37px]' : 'rounded-full'
            )}
          >
            <button
              type="button"
              className="m-2 flex w-full items-center rounded-full border bg-white p-4"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              aria-expanded={isDropdownOpen}
            >
              <span className="w-full text-center font-bold">
                {activeIndex !== null && !!tabs?.[activeIndex]
                  ? tabs[activeIndex].text?.value || ''
                  : t('system.SelectFeature')}
              </span>
              <JabraIcon
                Icon={icon_chevron_down}
                className={`ml-auto size-5 transition-transform ${isDropdownOpen ? 'rotate-180 transform' : ''}`}
              />
            </button>
          </div>

          {isDropdownOpen && (
            <div className="absolute z-10 w-full rounded-b-[37px] bg-[var(--tab-bg-color)] p-2 shadow-lg">
              <div className="flex flex-col gap-2">
                {tabs?.map((tab, index) => (
                  <React.Fragment key={`dropdown-tab-${tab.id}`}>
                    {activeIndex !== index && (
                      <button
                        type="button"
                        className={classNames(
                          'w-full rounded-full p-4 text-center font-bold text-[var(--tab-text-color)]',
                          'hover:bg-[var(--tab-darker-bg-color)] hover:text-[var(--tab-darker-text-color)]'
                        )}
                        onClick={() => {
                          handleTabClick(index);
                          setIsDropdownOpen(false);
                        }}
                        role="tab"
                        aria-selected={false}
                        aria-controls={`tabpanel-${instanceId}-${index}`}
                        id={`tab-${instanceId}-${index}`}
                      >
                        {tab.text?.value}
                      </button>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      );
  }
};

export default MobileTabNavigation;
